import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Display, H2, H3, H4, Lead, Small } from '@/components/ui/typography'
import { useTranslation } from 'react-i18next'
import { useLang } from '@/hooks/useLang'
import { generateSeo } from '@/lib/seo'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import {
  ChartLineIcon,
  AirplaneTiltIcon,
  ShieldWarningIcon,
  SoccerBallIcon,
  NewspaperIcon,
  HeartbeatIcon,
  BriefcaseIcon,
  MusicNoteIcon,
  PhoneCallIcon,
} from '@phosphor-icons/react'
import type { Icon as PhosphorIcon } from '@phosphor-icons/react'
import OpenClawIcon from '@/assets/openclaw-color.svg?react'
import OpenClawText from '@/assets/openclaw-text.svg?react'

const CATEGORY_CONFIG: Record<string, { icon: PhosphorIcon; color: string; bg: string; gradient: string }> = {
  finance: { icon: ChartLineIcon, color: 'text-emerald-600', bg: 'bg-emerald-100 dark:bg-emerald-900/30', gradient: 'from-emerald-500/10 to-transparent' },
  travel: { icon: AirplaneTiltIcon, color: 'text-sky-600', bg: 'bg-sky-100 dark:bg-sky-900/30', gradient: 'from-sky-500/10 to-transparent' },
  emergency: { icon: ShieldWarningIcon, color: 'text-rose-600', bg: 'bg-rose-100 dark:bg-rose-900/30', gradient: 'from-rose-500/10 to-transparent' },
  sports: { icon: SoccerBallIcon, color: 'text-orange-600', bg: 'bg-orange-100 dark:bg-orange-900/30', gradient: 'from-orange-500/10 to-transparent' },
  news: { icon: NewspaperIcon, color: 'text-violet-600', bg: 'bg-violet-100 dark:bg-violet-900/30', gradient: 'from-violet-500/10 to-transparent' },
  health: { icon: HeartbeatIcon, color: 'text-pink-600', bg: 'bg-pink-100 dark:bg-pink-900/30', gradient: 'from-pink-500/10 to-transparent' },
  business: { icon: BriefcaseIcon, color: 'text-indigo-600', bg: 'bg-indigo-100 dark:bg-indigo-900/30', gradient: 'from-indigo-500/10 to-transparent' },
  entertainment: { icon: MusicNoteIcon, color: 'text-amber-600', bg: 'bg-amber-100 dark:bg-amber-900/30', gradient: 'from-amber-500/10 to-transparent' },
}

const CATEGORY_KEYS = Object.keys(CATEGORY_CONFIG)

export const Route = createFileRoute('/$lang/openclaw/use-cases/$category')({
  component: UseCasesCategoryPage,
  loader: ({ params }) => {
    if (!CATEGORY_CONFIG[params.category]) {
      throw notFound()
    }
  },
  head: ({ match }) => {
    const category = match.params.category
    const name = CATEGORY_CONFIG[category] ? category.charAt(0).toUpperCase() + category.slice(1) : 'Use Cases'
    const { meta, links } = generateSeo({
      title: `${name} Voice Agent Use Cases — OpenClaw + PhoneSalad`,
      description: `Explore ${name.toLowerCase()} use cases for AI voice agents. See real examples of how OpenClaw agents make phone calls for ${name.toLowerCase()} scenarios.`,
      pathname: match.pathname,
    })
    return { meta, links }
  },
})

function UseCasesCategoryPage() {
  const { t } = useTranslation('openclaw')
  const lang = useLang()
  const { category } = Route.useParams()

  const config = CATEGORY_CONFIG[category]
  if (!config) return null

  const Icon = config.icon
  const name = t(`openclaw.useCasesPage.categories.${category}.name`)
  const description = t(`openclaw.useCasesPage.categories.${category}.description`)
  const examples = t(`openclaw.useCasesPage.categories.${category}.examples`, { returnObjects: true }) as {
    title: string
    description: string
    highlighted?: string
  }[]

  // Find adjacent categories for navigation
  const currentIndex = CATEGORY_KEYS.indexOf(category)
  const prevCategory = currentIndex > 0 ? CATEGORY_KEYS[currentIndex - 1] : null
  const nextCategory = currentIndex < CATEGORY_KEYS.length - 1 ? CATEGORY_KEYS[currentIndex + 1] : null

  return (
    <div className="min-h-screen bg-background">
      {/* Hero with gradient */}
      <section className={`py-20 lg:py-28 px-4 bg-gradient-to-b ${config.gradient}`}>
        <div className="container mx-auto max-w-4xl">
          <Link
            to="/$lang/openclaw/use-cases"
            params={{ lang }}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" /> {t('openclaw.allUseCases')}
          </Link>
          <div className="flex items-center gap-4 mb-6">
            <div className={`w-16 h-16 rounded-2xl ${config.bg} flex items-center justify-center`}>
              <Icon weight="duotone" className={`w-8 h-8 ${config.color}`} />
            </div>
            <div>
              <Display>{name}</Display>
            </div>
          </div>
          <Lead className="max-w-2xl">{description}</Lead>
        </div>
      </section>

      {/* Examples Grid */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {examples.map((example, i) => (
              <Card
                key={i}
                variant="outline"
                className={`flex flex-col ${example.highlighted ? 'ring-2 ring-primary/20 border-primary/30' : ''}`}
              >
                <CardContent className="flex flex-col flex-1">
                  <div className="flex items-start justify-between mb-3">
                    <div className={`w-10 h-10 rounded-lg ${config.bg} flex items-center justify-center shrink-0`}>
                      <PhoneCallIcon weight="duotone" className={`w-5 h-5 ${config.color}`} />
                    </div>
                    {example.highlighted && (
                      <Badge variant="secondary" className="text-xs">{t('openclaw.popular')}</Badge>
                    )}
                  </div>
                  <H4 className="mb-2 leading-snug agent-quote">{example.title}</H4>
                  <Small className="text-muted-foreground mt-auto">{example.description}</Small>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Adjacent Category Navigation */}
      <section className="py-12 px-4 border-t">
        <div className="container mx-auto max-w-6xl flex items-center justify-between">
          {prevCategory ? (
            <Link
              to="/$lang/openclaw/use-cases/$category"
              params={{ lang, category: prevCategory }}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {t(`openclaw.useCasesPage.categories.${prevCategory}.name`)}
            </Link>
          ) : <div />}
          {nextCategory ? (
            <Link
              to="/$lang/openclaw/use-cases/$category"
              params={{ lang, category: nextCategory }}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {t(`openclaw.useCasesPage.categories.${nextCategory}.name`)}
              <ArrowRight className="w-4 h-4" />
            </Link>
          ) : <div />}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="container mx-auto max-w-2xl text-center">
          <H2 className="mb-4">{t('openclaw.cta.title')}</H2>
          <Lead className="mb-8">{t('openclaw.cta.description')}</Lead>
          <Button size="lg" asChild>
            <Link to="/$lang/openclaw/setup" params={{ lang }}>
              {t('openclaw.cta.button')} <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  )
}
